import React, { useEffect, useRef } from 'react';
import L from '../../utils/leafletSetup';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import {
  GROUP_META, CZ_CENTER, CZ_ZOOM, escapeHtml, formatAddress, machineDateLabel,
  modelSummary, statusLabel, telHref, isEmail,
} from '../../utils/clientMap';

const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';
const KRAJE_URL = '/geo/kraje.geojson';

function markerIcon(group, approximate) {
  const color = GROUP_META[group]?.color || '#666';
  const ring = approximate ? 'border-style:dashed;' : '';
  return L.divIcon({
    className: 'client-map-marker',
    html: `<span style="display:block;width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45);${ring}"></span>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
    tooltipAnchor: [12, 0],
  });
}

function popupHtml(c) {
  const e = escapeHtml;
  const row = (label, value) => (value ? `<div><span style="color:#666">${label}:</span> ${value}</div>` : '');
  const phone = c.contact.phone ? `<a href="${e(telHref(c.contact.phone))}">${e(c.contact.phone)}</a>` : '';
  const email = isEmail(c.contact.email) ? `<a href="mailto:${e(c.contact.email.trim())}">${e(c.contact.email)}</a>` : e(c.contact.email);
  const machines = c.machines.map((m) => {
    const meta = GROUP_META[m.group];
    return `<li style="margin:4px 0">
      <strong>${e(m.model_type)}</strong>
      <span style="background:${meta.bg};color:${meta.color};padding:1px 6px;border-radius:4px;font-size:11px;font-weight:600">${e(statusLabel(m.status))}</span>
      <div style="color:#666;font-size:12px">${e(machineDateLabel(m))}${m.serial_number ? ` · SN ${e(m.serial_number)}` : ''}</div>
    </li>`;
  }).join('');
  const approx = c.geocode.precision === 'city'
    ? '<div style="color:#F57C00;font-size:12px;margin-top:4px">Poloha je jen přibližná (obec)</div>' : '';
  return `<div style="font-family:Inter,sans-serif;font-size:13px;line-height:1.45;min-width:220px">
    <div style="font-weight:700;font-size:15px;color:#333">${e(c.name)}</div>
    <button type="button" data-edit-address="${e(c.key)}" style="margin:6px 0 4px;padding:5px 10px;border:1px solid #E53935;border-radius:6px;background:#FFF;color:#E53935;font-size:12px;font-weight:600;cursor:pointer">✎ Upravit adresu</button>
    ${c.name !== c.company_name ? `<div style="color:#666">${e(c.company_name)}</div>` : ''}
    ${row('IČO', e(c.ico))}
    ${row('Adresa', e(formatAddress(c.address)))}
    ${row('Kontakt', e(c.contact.name))}
    ${row('Telefon', phone)}
    ${row('E-mail', email)}
    <div style="margin-top:6px"><span style="color:#666">Trenažéry:</span> ${e(modelSummary(c.machines))}</div>
    <ul style="margin:0;padding-left:16px">${machines}</ul>
    ${approx}
    <a href="/customers/${Number(c.id)}" data-client-detail="${Number(c.id)}" style="display:inline-block;margin-top:8px;color:#E53935;font-weight:600;text-decoration:none">Otevřít detail klienta →</a>
  </div>`;
}

const MapView = ({ clients, groupFor, focus, editing, onEditMove, onOpenDetail, onEditAddress, showKraje, onKrajeAvailable }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const markersRef = useRef(new Map());
  const krajeRef = useRef(null);
  const editMarkerRef = useRef(null);
  const handlersRef = useRef({});
  useEffect(() => { handlersRef.current = { onEditMove, onOpenDetail, onEditAddress }; }, [onEditMove, onOpenDetail, onEditAddress]);

  // Init map once
  useEffect(() => {
    const map = L.map(elRef.current, { center: CZ_CENTER, zoom: CZ_ZOOM, minZoom: 6, maxZoom: 18 });
    // The app sends "Referrer-Policy: no-referrer" (helmet); OSM blocks tile requests without a Referer,
    // so the tile images override it.
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19, referrerPolicy: 'strict-origin-when-cross-origin' }).addTo(map);
    const cluster = L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 45, spiderfyOnMaxZoom: true });
    map.addLayer(cluster);
    // SPA navigation from the popup link
    map.on('popupopen', (ev) => {
      const link = ev.popup.getElement()?.querySelector('[data-client-detail]');
      if (link) link.onclick = (e) => { e.preventDefault(); handlersRef.current.onOpenDetail(Number(link.dataset.clientDetail)); };
      const edit = ev.popup.getElement()?.querySelector('[data-edit-address]');
      if (edit) edit.onclick = () => { map.closePopup(); handlersRef.current.onEditAddress(edit.dataset.editAddress); };
    });
    map.on('click', (ev) => { if (editMarkerRef.current) { editMarkerRef.current.setLatLng(ev.latlng); handlersRef.current.onEditMove(ev.latlng.lat, ev.latlng.lng); } });
    mapRef.current = map;
    clusterRef.current = cluster;
    // Map is often mounted in a flex container whose size settles after first paint
    const t = setTimeout(() => map.invalidateSize(), 100);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; };
  }, []);

  // Markers
  useEffect(() => {
    const cluster = clusterRef.current;
    cluster.clearLayers();
    markersRef.current = new Map();
    const markers = clients.filter((c) => c.location).map((c) => {
      const marker = L.marker([c.location.lat, c.location.lng], { icon: markerIcon(groupFor(c), c.geocode.precision === 'city'), title: c.name });
      marker.bindTooltip(escapeHtml(c.name), { direction: 'right' });
      marker.bindPopup(() => popupHtml(c), { maxWidth: 320 });
      markersRef.current.set(c.key, marker);
      return marker;
    });
    cluster.addLayers(markers);
  }, [clients, groupFor]);

  // Focus a client (click in list)
  useEffect(() => {
    if (!focus) return;
    const marker = markersRef.current.get(focus.key);
    if (marker) clusterRef.current.zoomToShowLayer(marker, () => marker.openPopup());
  }, [focus]);

  // Manual placement: draggable marker
  useEffect(() => {
    const map = mapRef.current;
    if (!editing) return undefined;
    const start = editing.lat != null ? [editing.lat, editing.lng] : map.getCenter();
    const marker = L.marker(start, { draggable: true, autoPan: true, zIndexOffset: 1000, icon: markerIcon('objednano', false) }).addTo(map);
    marker.bindTooltip('Přetáhněte na správné místo', { permanent: true, direction: 'top', offset: [0, -14] });
    marker.on('dragend', () => { const p = marker.getLatLng(); handlersRef.current.onEditMove(p.lat, p.lng); });
    editMarkerRef.current = marker;
    map.setView(start, Math.max(map.getZoom(), editing.lat != null ? 15 : 8));
    return () => { marker.remove(); editMarkerRef.current = null; };
  }, [editing?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep draggable marker in sync with typed coordinates
  useEffect(() => {
    if (editMarkerRef.current && editing?.lat != null && editing?.lng != null) {
      editMarkerRef.current.setLatLng([editing.lat, editing.lng]);
    }
  }, [editing?.lat, editing?.lng]);

  // Region borders (optional GeoJSON file)
  useEffect(() => {
    const map = mapRef.current;
    if (!showKraje) { krajeRef.current?.remove(); return; }
    if (krajeRef.current) { krajeRef.current.addTo(map); return; }
    let cancelled = false;
    fetch(KRAJE_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((geo) => {
        if (cancelled || !mapRef.current) return;
        krajeRef.current = L.geoJSON(geo, { interactive: false, style: { color: '#E53935', weight: 1.5, opacity: 0.7, fillOpacity: 0.03 } }).addTo(map);
      })
      .catch(() => onKrajeAvailable?.(false));
    return () => { cancelled = true; };
  }, [showKraje, onKrajeAvailable]);

  return <div ref={elRef} role="region" aria-label="Mapa klientů" style={{ width: '100%', height: '100%', minHeight: '420px', borderRadius: '12px' }} />;
};

export default MapView;
